/**
 * Campanhas dos revendedores e o funil do link.
 *
 * As mesmas telas de relatório servem ao revendedor (só as campanhas dele) e
 * ao admin (todas, com filtro por revendedor): o que muda é a raiz da rota,
 * `/reseller/...` ou `/admin/reports/...`. A API é quem recorta — o painel do
 * revendedor nunca manda id de revendedor.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";

import type {
  Campaign,
  CampaignDetailReport,
  CampaignFilters,
  CampaignsReport,
  ResellerRankingReport,
} from "@montebox/shared/campaigns";

import { api } from "@/lib/api";
import type { PeriodFilter } from "@/lib/reports";

export type {
  Campaign,
  CampaignDetailReport,
  CampaignMetrics,
  CampaignRow,
  CampaignsReport,
  ResellerRankingReport,
  ResellerRankingRow,
} from "@montebox/shared/campaigns";

/** Quem está olhando: define a raiz das rotas e o que a API devolve. */
export type CampaignSide = "reseller" | "admin";

/** Filtros da tela: período obrigatório; o resto some da URL quando vazio. */
export type CampaignQuery = PeriodFilter & {
  [K in Exclude<keyof CampaignFilters, "from" | "to">]?: CampaignFilters[K] | undefined;
};

const REPORTS: Record<CampaignSide, string> = {
  reseller: "/reseller/reports/campaigns",
  admin: "/admin/reports/campaigns",
};

const query = (filters: Partial<CampaignQuery>): string => {
  const params = new URLSearchParams(
    Object.entries(filters).filter((entry): entry is [string, string] => Boolean(entry[1])),
  );
  const text = params.toString();
  return text ? `?${text}` : "";
};

export const campaignKeys = {
  list: ["reseller", "campaigns"] as const,
  report: (side: CampaignSide, filters: CampaignQuery) =>
    [side === "admin" ? "admin" : "reseller", "campaign-report", filters] as const,
  detail: (side: CampaignSide, id: string, period: PeriodFilter) =>
    [side === "admin" ? "admin" : "reseller", "campaign-detail", id, period] as const,
  ranking: (period: PeriodFilter) => ["admin", "reports", "resellers", period] as const,
};

// ------------------------------- Relatórios ----------------------------------

export function useCampaignReport(
  side: CampaignSide,
  filters: CampaignQuery,
): UseQueryResult<CampaignsReport> {
  return useQuery({
    queryKey: campaignKeys.report(side, filters),
    queryFn: () => api.get<CampaignsReport>(`${REPORTS[side]}${query(filters)}`),
    placeholderData: (previous) => previous,
  });
}

export function useCampaignDetail(
  side: CampaignSide,
  id: string,
  period: PeriodFilter,
): UseQueryResult<CampaignDetailReport> {
  return useQuery({
    queryKey: campaignKeys.detail(side, id, period),
    queryFn: () => api.get<CampaignDetailReport>(`${REPORTS[side]}/${id}${query(period)}`),
    placeholderData: (previous) => previous,
  });
}

export const downloadCampaigns = (side: CampaignSide, filters: CampaignQuery): Promise<void> =>
  api.download(
    `${REPORTS[side]}/export${query(filters)}`,
    `campanhas_${filters.from}_a_${filters.to}.csv`,
  );

export function useResellerRanking(period: PeriodFilter): UseQueryResult<ResellerRankingReport> {
  return useQuery({
    queryKey: campaignKeys.ranking(period),
    queryFn: () => api.get<ResellerRankingReport>(`/admin/reports/resellers${query(period)}`),
    placeholderData: (previous) => previous,
  });
}

// ------------------------- Cadastro (revendedor) -----------------------------

export interface NewCampaign {
  name: string;
  slug?: string;
  utmSource: string;
  utmMedium: string;
  utmCampaign?: string;
  utmContent?: string;
}

export function useCampaigns(): UseQueryResult<Campaign[]> {
  return useQuery({
    queryKey: campaignKeys.list,
    queryFn: () => api.get<Campaign[]>("/reseller/campaigns"),
  });
}

function useInvalidateReseller() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ["reseller"] });
}

export function useCreateCampaign() {
  const invalidate = useInvalidateReseller();
  return useMutation({
    mutationFn: (data: NewCampaign) => api.post<Campaign>("/reseller/campaigns", data),
    onSuccess: invalidate,
  });
}

export function useUpdateCampaign() {
  const invalidate = useInvalidateReseller();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string; name?: string; archived?: boolean }) =>
      api.patch<Campaign>(`/reseller/campaigns/${id}`, data),
    onSuccess: invalidate,
  });
}

/** Endereço completo do link da campanha, no domínio em que a loja está. */
export const campaignUrl = (code: string, slug: string): string =>
  `${window.location.origin}/r/${code}/${slug}`;

/** Sugestões de origem e meio — as mais comuns, para não virar "Insta" e "insta". */
export const UTM_SOURCES = ["instagram", "whatsapp", "facebook", "tiktok", "youtube", "google"];
export const UTM_MEDIUMS = ["stories", "bio", "post", "reels", "grupo", "status", "anuncio"];

// ------------------------------ Filtros na URL -------------------------------

/**
 * Filtros de campanha na URL, com nomes em português como o resto do painel:
 * relatório é coisa que se compartilha e que se reabre depois de um F5.
 */
const SEARCH: Record<string, keyof CampaignQuery> = {
  de: "from",
  ate: "to",
  revendedor: "resellerId",
  campanha: "campaignId",
  situacao: "status",
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_content: "utmContent",
  peca: "pieceId",
  categoria: "categoryId",
};

export type CampaignSearch = Partial<Record<string, string>>;

export const validateCampaignSearch = (search: Record<string, unknown>): CampaignSearch => {
  const out: CampaignSearch = {};
  for (const key of Object.keys(SEARCH)) {
    const value = search[key];
    if (typeof value === "string" && value) out[key] = value;
  }
  if (out["situacao"] && out["situacao"] !== "ativa" && out["situacao"] !== "arquivada") {
    delete out["situacao"];
  }
  return out;
};

export const searchToQuery = (search: CampaignSearch, fallback: PeriodFilter): CampaignQuery => {
  const query: Record<string, string> = { from: fallback.from, to: fallback.to };
  for (const [param, key] of Object.entries(SEARCH)) {
    const value = search[param];
    if (value) query[key] = value;
  }
  // `validateCampaignSearch` já limpou os valores; aqui só se troca o nome.
  return query as unknown as CampaignQuery;
};

export const queryToSearch = (query: CampaignQuery): CampaignSearch => {
  const out: CampaignSearch = {};
  for (const [param, key] of Object.entries(SEARCH)) {
    const value = query[key];
    if (value) out[param] = value;
  }
  return out;
};
